import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { FeatureFlagKey } from 'services/statsig/types';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import theme from 'shared/styles/theme';
import useVaroAdvanceFlow from 'shared/utils/useVaroAdvanceFlow/useVaroAdvanceFlow';

import IntroScreenContent from './IntroScreenContent';

const IntroScreen = () => {
  const isFocused = useIsFocused();
  const router = useRouter();
  const { source, from } = useLocalSearchParams<{
    source?: string;
    from?: string;
  }>();
  const dismissToTab = useDismissToRootTab();
  const { isFeatureEnabled } = useStatsig();
  const { segmentClient } = useAnalyticsConfiguration();
  // TODO: JT-136086 normalize enable_lightning
  const isLightningEnabled = isFeatureEnabled(FeatureFlagKey.ENABLE_LIGHTNING);

  const navigation = useNavigation();
  const routes = navigation.getState()?.routes;
  const prevRoute = routes?.[routes.length - 2];
  const flow = useVaroAdvanceFlow();

  useEffect(() => {
    if (!isLightningEnabled && isFocused) {
      // TODO: JT-136086 - removce when we normalize enable_lightning
      void segmentClient.logEvent('enable_lightning disabled', {
        name: 'enable_ligthning disabled',
        category: 'Advance',
        source_name: 'VA Day 0',
        alert_name: 'technical_issues',
        prevRoute: prevRoute?.name,
      });

      dismissToTab(Tabs.HOME);
    }
  }, [
    isLightningEnabled,
    prevRoute?.name,
    router,
    isFocused,
    segmentClient,
    dismissToTab,
    source,
    flow,
  ]);

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'lightningIntro',
  });

  const goHome = () => {
    if (from === 'discover-more') {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  const handleContinuePress: () => void = () => {
    const params = {
      ...(source && { source }),
      ...(from && { from }),
    };

    router.navigate({ pathname: `/${flow}/link-account`, params });
  };

  useBackHandler(goHome);

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} transparent primaryOnPress={goHome} />
          ),
        }}
      />
      <ProductIntroScreen
        backgroundTexture={backgroundTextureLight as ImageSourcePropType}
        headlineText={translatedText('headLineText')}
        headlineTextStyle="expressive"
        primaryButton={{
          label: translatedText('buttonContinue'),
          onPress: handleContinuePress,
          analyticsId: 'lightningIntro',
          customAttributes: {
            cta: 'Continue',
          },
        }}
        bodyContent={<IntroScreenContent />}
        visual="ADVANCE_A"
        topPadding={80}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default IntroScreen;
